import { Injectable } from "@angular/core";
import { localizeRuntimeById } from "@app/runtime-localize";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { CableRoutesTable } from "@pdf/export-services/product-information-pages/floorplan-information-pages/cable-routes/cable-routes-table";
import { FreeTextsTable } from "@pdf/export-services/product-information-pages/floorplan-information-pages/free-texts/free-texts-table";
import { ProtectiveTubesTable } from "@pdf/export-services/product-information-pages/floorplan-information-pages/protective-tubes/protective-tubes-table";
import { ReferenceMeasureTable } from "@pdf/export-services/product-information-pages/floorplan-information-pages/reference-measure/reference-measure-table";
import { WiresTable } from "@pdf/export-services/product-information-pages/floorplan-information-pages/wires/wires-table";
import { TableOfContents } from "@pdf/export-services/table-of-contents/table-of-contents";
import { HalfPageContainer } from "@pdf/helpers/container/half-page/half-page-container";
import { HalfPageContainerService } from "@pdf/helpers/container/half-page/half-page-container.service";
import { TableService } from "@pdf/helpers/default-table/table-service";
import { MAX_HEADLINE_WIDTH, truncateTextToWidth } from "@pdf/helpers/pdf-text.helper";
import { Headlines, PdfProperties } from "@pdf/pdf-properties";
import { jsPDF } from "jspdf";
import { AirPathsTable } from "./air-paths/air-paths-table";
import { MeasurementLinesTable } from "./measurement-lines/measurement-lines-table";
import { NotesTable } from "./notes/notes-table";
import { PipelinesTable } from "./pipelines/pipelines-table";
import { ZonesTable } from "./zones/zones-table";

@Injectable({
  providedIn: "root",
})
export class FloorplanInformationPdfService {
  readonly headlines = new Headlines();
  containerService: HalfPageContainerService;
  tableService: TableService;
  constructor() {
    this.containerService = new HalfPageContainerService();
    this.tableService = new TableService();
  }

  private getDangerAreaTypeText() {
    return localizeRuntimeById("global.dangerArea", "Gefahrenbereich");
  }

  private isDataAvailable(floorplan: Floorplan) {
    return (
      floorplan.exZones.length > 0 ||
      floorplan.dustExZones.length > 0 ||
      floorplan.wildcardZones.length > 0 ||
      floorplan.dangerAreas.length > 0 ||
      floorplan.pipelines.length > 0 ||
      floorplan.cableRoutes.length > 0 ||
      floorplan.protectiveTubes.length > 0 ||
      floorplan.wires.length > 0 ||
      floorplan.measurementLines.length > 0 ||
      floorplan.airPaths.length > 0 ||
      floorplan.floorplanTexts.length > 0 ||
      floorplan.referenceMeasure ||
      floorplan.isolationRooms.length > 0 ||
      floorplan.itRooms.length > 0 ||
      floorplan.operatingTheatres.length > 0 ||
      floorplan.procedureRooms.length > 0 ||
      floorplan.standardRooms.length > 0
    );
  }
  public generatePages(pdf: jsPDF, floorplan: Floorplan, container: HalfPageContainer, tableOfContents: TableOfContents) {
    const prefix = `${this.headlines.FLOORPLAN_INFORMATION} | `;
    const maxWidth = MAX_HEADLINE_WIDTH;
    pdf.setFontSize(PdfProperties.HEADLINE_FONT_SIZE);
    const truncatedName = truncateTextToWidth(pdf, floorplan.name, maxWidth - pdf.getTextWidth(prefix));
    const headline = `${prefix}${truncatedName}`;

    if (this.isDataAvailable(floorplan)) {
      this.containerService.addOnNewPage(pdf, container, headline, tableOfContents);
    }
    if (
      floorplan.exZones.length ||
      floorplan.dustExZones.length ||
      floorplan.dangerAreas.length ||
      floorplan.wildcardZones.length ||
      floorplan.isolationRooms.length ||
      floorplan.itRooms.length ||
      floorplan.operatingTheatres.length ||
      floorplan.procedureRooms.length ||
      floorplan.standardRooms.length
    ) {
      this.tableService.tableToPdf(
        pdf,
        new ZonesTable(
          pdf,
          floorplan.exZones,
          floorplan.dustExZones,
          floorplan.dangerAreas,
          floorplan.wildcardZones,
          floorplan.isolationRooms,
          floorplan.itRooms,
          floorplan.operatingTheatres,
          floorplan.procedureRooms,
          floorplan.standardRooms,
          this.getDangerAreaTypeText(),
        ),
        container,
        tableOfContents,
        headline,
      );
    }
    if (floorplan.pipelines.length) {
      this.tableService.tableToPdf(pdf, new PipelinesTable(pdf, floorplan.pipelines), container, tableOfContents, headline);
    }
    if (floorplan.cableRoutes.length) {
      this.tableService.tableToPdf(pdf, new CableRoutesTable(pdf, floorplan.cableRoutes), container, tableOfContents, headline);
    }
    if (floorplan.protectiveTubes.length) {
      this.tableService.tableToPdf(
        pdf,
        new ProtectiveTubesTable(pdf, floorplan.protectiveTubes),
        container,
        tableOfContents,
        headline,
      );
    }
    if (floorplan.wires.length) {
      this.tableService.tableToPdf(pdf, new WiresTable(pdf, floorplan.wires), container, tableOfContents, headline);
    }
    if (floorplan.measurementLines.length) {
      this.tableService.tableToPdf(
        pdf,
        new MeasurementLinesTable(pdf, floorplan.measurementLines),
        container,
        tableOfContents,
        headline,
      );
    }
    if (floorplan.referenceMeasure) {
      this.tableService.tableToPdf(
        pdf,
        new ReferenceMeasureTable(pdf, floorplan.referenceMeasure),
        container,
        tableOfContents,
        headline,
      );
    }
    if (floorplan.airPaths.length) {
      this.tableService.tableToPdf(pdf, new AirPathsTable(pdf, floorplan.airPaths), container, tableOfContents, headline);
    }

    if (floorplan.floorplanTexts.length) {
      this.tableService.tableToPdf(pdf, new NotesTable(pdf, floorplan.floorplanTexts), container, tableOfContents, headline);
    }
    if (floorplan.floorplanFreeTexts.length) {
      this.tableService.tableToPdf(
        pdf,
        new FreeTextsTable(pdf, floorplan.floorplanFreeTexts),
        container,
        tableOfContents,
        headline,
      );
    }
  }
}
